import {
  Auth,
  ErrorResponse,
  LoginParams,
  LoginResponse,
  PlatformsAuthUrlParams,
  PlatformsAuthUrlResponse,
} from '@orca/api-auth';
import { useReCaptcha } from 'next-recaptcha-v3';
import * as React from 'react';
import { ReactNode } from 'react';

import { LOCALSTORAGE_KEYS } from '~/constants/local_storage';
import { routes } from '~/constants/routes';
import { AuthTokens } from '~/hooks/useImpersonate.d';
import { getMainRoleUser } from '~/utils/getMainRoleUser';
import { getUserIdFromAccessToken } from '~/utils/getUserIdFromAccessToken';
import { isValidLoginRole } from '~/utils/isValidLoginRole';
import { setTrackerUser } from '~/utils/user';

import { SimpleSendVerifyEmailParams } from './useAuth.vm';

interface User {
  id: string;
  role: string;
}

interface AuthContextInterface {
  login: (
    params: Omit<LoginParams, 'recaptcha'>,
    redirectUrl?: string
  ) => Promise<void>;
  setLoggedInUser: (tokens: AuthTokens, redirectUrl?: string) => void;
  platformsAuthUrl: (
    params: PlatformsAuthUrlParams
  ) => Promise<ErrorResponse | PlatformsAuthUrlResponse>;
  loginWithSSOAccount: (
    params: AuthTokens,
    redirectUrl?: string
  ) => Promise<void>;
  sendVerifyEmail: (params: SimpleSendVerifyEmailParams) => Promise<void>;
  user: User;
}

const AuthContext = React.createContext<AuthContextInterface | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = React.useState<User>({ id: '', role: '' });
  const { executeRecaptcha } = useReCaptcha();

  const persistTokens = (tokens: AuthTokens) => {
    window.localStorage.setItem(
      LOCALSTORAGE_KEYS.accessTokenKey,
      tokens.accessToken
    );
    window.localStorage.setItem(
      LOCALSTORAGE_KEYS.refreshTokenKey,
      tokens.refreshToken
    );
  };

  const login = async (
    params: Omit<LoginParams, 'recaptcha'>,
    redirectUrl?: string
  ) => {
    const recaptchaToken = await executeRecaptcha('login');

    const resp: any = await Auth.login({
      ...params,
      recaptcha: recaptchaToken,
    });

    if (Number.isInteger(resp.errorCode)) {
      throw new Error(resp.errorCode.toString());
    } else {
      const { accessToken, refreshToken } = resp as LoginResponse;
      if (!isValidLoginRole(accessToken)) {
        throw new Error('INVALID_ROLE');
      }
      setLoggedInUser(
        { accessToken, refreshToken },
        redirectUrl || routes.default
      );
    }
  };

  const setLoggedInUser = (tokens: AuthTokens, redirectUrl?: string) => {
    persistTokens(tokens);
    setUser({
      id: getUserIdFromAccessToken(tokens.accessToken),
      role: getMainRoleUser(tokens.accessToken),
    });
    setTrackerUser({ accessToken: tokens.accessToken });
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    }
  };

  const platformsAuthUrl = async (params: PlatformsAuthUrlParams) => {
    const resp: any = await Auth.platformsAuthUrl(params);
    if (Number.isInteger(resp.errorCode)) {
      throw new Error(resp.errorCode);
    } else {
      return resp as PlatformsAuthUrlResponse;
    }
  };

  const loginWithSSOAccount = (tokens: AuthTokens, redirectUrl?: string) => {
    setLoggedInUser(tokens, redirectUrl || routes.default);
  };

  const sendVerifyEmail = async (params: SimpleSendVerifyEmailParams) => {
    const recaptchaToken = await executeRecaptcha('sendVerifyEmail');

    await Auth.sendVerifyEmail({
      ...params,
      recaptcha: recaptchaToken,
    });
  };

  const providerValue = {
    login,
    platformsAuthUrl,
    sendVerifyEmail,
    loginWithSSOAccount,
    setLoggedInUser,
    user,
  } as AuthContextInterface;

  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};
